import { myPushSubscriptionPOST } from "./api";

// The browser side of web push: register the worker, ask permission, subscribe
// this phone and tell the server. Only ever called for a signed-in user whose
// account has push switched on, so no other browser gets a worker.

export function pushSupported() {
	return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

export function pushPermission() {
	return pushSupported() ? Notification.permission : "unsupported";
}

// iPhone: Safari shows push only from a web app installed to the Home Screen
// and opened from there. Tell her that rather than failing quietly.
export function isIOS() {
	return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

export function isStandalone() {
	return window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
}

export function registerWorker() {
	if (!("serviceWorker" in navigator)) {
		return Promise.reject(new Error("This browser cannot show notifications."));
	}
	return navigator.serviceWorker.register("/service-worker.js").then(() => navigator.serviceWorker.ready);
}

// The key travels as base64url and has to reach the browser as bytes.
function keyBytes(base64) {
	const padded = (base64 + "=".repeat((4 - (base64.length % 4)) % 4)).replace(/-/g, "+").replace(/_/g, "/");
	const raw = atob(padded);
	const out = new Uint8Array(raw.length);
	for (let i = 0; i < raw.length; i++) {
		out[i] = raw.charCodeAt(i);
	}
	return out;
}

// The subscription this browser already holds, if any.
export function currentSubscription() {
	if (!pushSupported()) {
		return Promise.resolve(null);
	}
	return registerWorker()
		.then(reg => reg.pushManager.getSubscription())
		.catch(() => null);
}

// Ask, subscribe, post. Must run from a click: Safari refuses the permission
// prompt otherwise. Resolves with the server's {id}.
export function subscribeThisPhone(publicKey) {
	if (!pushSupported()) {
		return Promise.reject(new Error("This browser cannot show notifications."));
	}
	if (!publicKey) {
		return Promise.reject(new Error("Push is not set up on this server yet."));
	}
	return Notification.requestPermission().then(permission => {
		if (permission !== "granted") {
			throw new Error("Notifications were not allowed, so nothing was switched on. Press the button again to be asked once more.");
		}
		return registerWorker()
			.then(reg => reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(publicKey) }))
			.then(sub => {
				const body = sub.toJSON();
				body.user_agent = describeDevice();
				return myPushSubscriptionPOST(body);
			})
			.then(r => r.data);
	});
}

// Drop this browser's subscription locally. The server row is removed by the
// caller (it knows the id); the browser forgetting it is the part only the
// browser can do.
export function unsubscribeThisPhone() {
	return currentSubscription().then(sub => (sub ? sub.unsubscribe() : false));
}

// A short, name-free label for the device list: "Android phone, Chrome".
export function describeDevice() {
	const ua = navigator.userAgent;
	let device = "This browser";
	if (/Android/i.test(ua)) device = "Android phone";
	else if (isIOS()) device = /iPad/.test(ua) ? "iPad" : "iPhone";
	else if (/Windows/i.test(ua)) device = "Windows computer";
	else if (/Macintosh/i.test(ua)) device = "Mac";
	let browser = "";
	if (/Edg\//.test(ua)) browser = "Edge";
	else if (/Chrome\//.test(ua) && !/Edg\//.test(ua)) browser = "Chrome";
	else if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) browser = "Safari";
	else if (/Firefox\//.test(ua)) browser = "Firefox";
	return browser ? device + ", " + browser : device;
}
